import { Alert, Button, InputField } from '@design-system';
import { isPrefersReducedMotion } from '@utils/isPrefersReducedMotion';
import classNames from 'classnames';
import { motion, useReducedMotion } from 'framer-motion';
import type {
  GetStaticPropsContext,
  GetStaticPropsResult,
  InferGetStaticPropsType,
} from 'next';
import { useTranslation } from 'next-i18next';
import {
  CSSProperties,
  forwardRef,
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';

import {
  LazyCloudflareTurnstile,
  useResetTurnstile,
} from '../../../components/Captcha/LazyCloudflareTurnstile';
import StickyHeader from '../../../components/Containers/StickyHeader';
import { Image } from '../../../components/Image';
import Layout from '../../../components/Layout';
import ScrollAnchor from '../../../components/ScrollAnchor';
import { imageOgStaticLoader } from '../../../helpers/og-image';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { Franchises, HeroImage } from '../../../types/Franchises';
import {
  GeneratedHubspotLists,
  useHubspotListMutation,
} from '../../../types/generated';
import {
  getStoryblokPage,
  StoryResponse,
  useStoryblok,
} from '../../../utils/storyblok';
import { pageTranslations } from '../../../utils/translations';

type PageProps = {
  locale: string;
  locales: string[];
  ogImageUrl: string;
  preview: boolean;
  story: Franchises;
  backgroundImage: StoryblokImage;
};

const FONT_STYLES = {
  'xl': {
    fontSize: 'clamp(2.25rem, 3.409vw + 1.227rem, 4.5rem)',
    lineHeight: 'clamp(2.25rem, 3.409vw + 1.227rem, 4.5rem)',
    letterSpacing: '-4%',
  },
  'lg': {
    fontSize: 'clamp(1.5rem, 2.273vw + 0.818rem, 3rem)',
    lineHeight: 'clamp(1.5rem, 2.273vw + 0.818rem, 3rem)',
    letterSpacing: '-2%',
  },
  'md': {
    fontSize: 'clamp(1rem, 0.758vw + 0.773rem, 1.5rem)',
    lineHeight: 'clamp(1.25rem, 0.947vw + 0.966rem, 1.875rem)',
    letterSpacing: '-1%',
  },
  'base': {
    fontSize: '1rem',
    lineHeight: '1.5rem',
  },
  'xs': {
    fontSize: '0.75rem',
    lineHeight: '1rem',
  },
};

const scrollToShortcut = (ref: RefObject<HTMLElement>) => {
  ref?.current?.scrollIntoView({
    behavior: isPrefersReducedMotion() ? 'instant' : 'smooth',
  });
};

const SubscribeForm = forwardRef((_, ref) => {
  const { t } = useTranslation(['reports']);
  const [captchaToken, setCaptchaToken] = useState('');
  const { turnstileRef, turnstileReset } = useResetTurnstile();
  const [hubspotListMutation] = useHubspotListMutation();

  const form = useForm<{ email: string }>({
    criteriaMode: 'firstError',
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: { email: '' },
  });

  const { isValid } = form.formState;
  const [isLoading, setLoading] = useState(false);
  const [isSuccess, setSuccess] = useState(false);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      await hubspotListMutation({
        variables: {
          email: form.getValues().email,
          captcha: captchaToken,
          list: GeneratedHubspotLists.Whitepaper2026,
        },
      });
      setSuccess(true);
      form.reset();
    } catch (err) {
      setSuccess(false);
      turnstileReset();
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hubspotListMutation, captchaToken, form, turnstileReset]);

  useImperativeHandle(ref, () => ({
    focusEmailInput: () => {
      form.setFocus('email');
    },
  }));

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4">
      {isSuccess && (
        <Alert status="success">
          {t('reports:subscription.alert.success')}
        </Alert>
      )}

      <div className="w-full">
        <div className="flex w-full flex-col gap-3 md:flex-row">
          <InputField
            id="email"
            placeholder={t('reports:subscription.email.placeholder')}
            type="email"
            {...form.register('email', {
              required: true,
              pattern: /\S+@\S+\.\S+/,
            })}
          />

          <div className="md:shrink-0">
            <Button
              disabled={!isValid || isLoading}
              fullWidth
              label={t('reports:subscription.ctaLabel.default')}
              loading={isLoading}
              onClick={() => void handleSubmit()}
              size="md"
              variant="primary"
            />
          </div>
        </div>

        <LazyCloudflareTurnstile
          actionName="collector_report_subscribe"
          className="mt-4 flex w-full items-center justify-center"
          onError={() => setCaptchaToken('')}
          onExpire={() => setCaptchaToken('')}
          onSuccess={setCaptchaToken}
          turnstileRef={turnstileRef}
        />
      </div>
    </div>
  );
});

const BEFORE_CLASS_SOLID =
  'max-md:before:z-0 max-md:before:absolute max-md:before:top-0 max-md:before:block max-md:before:h-1/2 max-md:before:w-px max-md:before:self-center max-md:before:bg-divider/20 max-md:before:content-[""]';
const BEFORE_CLASS_GRADIENT =
  'max-md:before:z-0 max-md:before:absolute max-md:before:top-0 max-md:before:block max-md:before:h-16 max-md:before:w-px max-md:before:self-center max-md:before:bg-gradient-to-b max-md:before:from-divider/20 max-md:before:content-[""]';
const AFTER_CLASS_SOLID =
  'max-md:after:z-0 max-md:after:absolute max-md:after:bottom-0 max-md:after:block max-md:after:h-1/2 max-md:after:w-px max-md:after:self-center max-md:after:bg-divider/20 max-md:after:content-[""]';
const AFTER_CLASS_GRADIENT =
  'max-md:after:z-0 max-md:after:absolute max-md:after:bottom-0 max-md:after:block max-md:after:h-16 max-md:after:w-px max-md:after:self-center max-md:after:bg-gradient-to-t max-md:after:from-divider/20 max-md:after:content-[""]';

const Section = ({
  children,
  hideLine = false,
}: PropsWithChildren<{ hideLine?: boolean }>) => {
  return (
    <div
      className={classNames(
        'wrapper-website before:bottm-0 relative grid grid-cols-4 md:gap-12',
        {
          'md:before:absolute md:before:left-1/2 md:before:top-0 md:before:h-full md:before:w-px md:before:-translate-x-1/2 md:before:bg-divider/20 md:before:content-[""]':
            !hideLine,
        },
      )}
    >
      {children}
    </div>
  );
};

const SectionTitle = ({
  children,
  className,
  hideLine = false,
  overline,
}: PropsWithChildren<{
  overline: string;
  className?: string;
  hideLine?: boolean;
}>) => {
  return (
    <div
      className={classNames(
        'relative flex w-full max-w-120 flex-col items-center justify-center gap-2 justify-self-center py-24 sm:max-w-160',
        'md:bg-[linear-gradient(0deg,transparent_0%,white_25%,white_75%,transparent_100%)]',
        !hideLine && BEFORE_CLASS_GRADIENT,
        !hideLine && AFTER_CLASS_GRADIENT,
        className,
      )}
    >
      <span className="text-center" style={FONT_STYLES.base}>
        {overline}
      </span>
      <h2
        className="w-full text-balance text-center font-bold"
        style={FONT_STYLES.xl}
      >
        {children}
      </h2>
    </div>
  );
};

const SectionKeyPoint = ({
  className,
  index,
  text,
  title,
}: {
  index: number;
  title: string;
  text: string;
  className?: string;
}) => {
  return (
    <div
      className={classNames(
        'relative flex w-full max-w-120 flex-col justify-center gap-6 justify-self-center py-24',
        BEFORE_CLASS_GRADIENT,
        AFTER_CLASS_GRADIENT,
        className,
      )}
    >
      <span
        aria-hidden
        style={FONT_STYLES.md}
        className="flex aspect-[1/1] items-center justify-center self-center rounded-full bg-active-4 p-6 font-bold normal-nums text-active-1"
      >
        {index}
      </span>
      <h3
        className="w-full text-balance text-center font-bold"
        style={FONT_STYLES.md}
      >
        {title}
      </h3>
      <p className="text-balance text-center" style={FONT_STYLES.base}>
        {text}
      </p>
    </div>
  );
};

const SectionChart = ({
  children,
  className,
  title,
}: PropsWithChildren<{
  title: string;
  className?: string;
}>) => {
  const shouldReduceMotion = useReducedMotion();
  return (
    <motion.figure
      className={classNames(
        'relative flex w-full max-w-120 flex-col items-center justify-center gap-4 justify-self-center py-24 sm:max-w-160',
        BEFORE_CLASS_SOLID,
        AFTER_CLASS_SOLID,
        className,
      )}
      initial={{ opacity: shouldReduceMotion ? 1 : 0 }}
      viewport={{ once: true }}
      whileInView={{
        opacity: 1,
        transition: {
          type: 'easeOut',
          duration: 0.8,
        },
      }}
    >
      <figcaption
        className="w-full max-w-[50%] self-start text-balance text-active-4 md:max-w-80"
        style={FONT_STYLES.base}
      >
        {title}
      </figcaption>
      {children}
    </motion.figure>
  );
};

const SectionImage = ({
  caption,
  children,
  className,
}: PropsWithChildren<{
  caption: string;
  className?: string;
}>) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.figure
      className={classNames(
        'relative flex w-full flex-col justify-center gap-2 justify-self-center py-24',
        BEFORE_CLASS_SOLID,
        AFTER_CLASS_SOLID,
        className,
      )}
      initial={{ opacity: shouldReduceMotion ? 1 : 0 }}
      viewport={{ once: true }}
      whileInView={{
        opacity: 1,
        transition: {
          type: 'easeOut',
          duration: 0.8,
        },
      }}
    >
      {children}
      <figcaption
        className="w-full max-w-72 text-balance text-neutral-4"
        style={FONT_STYLES.xs}
      >
        {caption}
      </figcaption>
    </motion.figure>
  );
};

const SectionQuote = ({
  author,
  className,
  quote,
}: {
  author: string;
  quote: string;
  className?: string;
}) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.figure
      className={classNames(
        'relative flex w-full max-w-120 flex-col items-center justify-center gap-2 justify-self-center px-6 py-24',
        BEFORE_CLASS_GRADIENT,
        AFTER_CLASS_GRADIENT,
        className,
      )}
      initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 24,
      }}
      viewport={{ once: true }}
      whileInView={{
        opacity: 1,
        y: 0,
        transition: {
          type: 'easeOut',
          duration: 0.8,
          delay: 0.2,
        },
      }}
    >
      <blockquote
        className="w-full text-balance text-center"
        style={FONT_STYLES.md}
      >
        {quote}
      </blockquote>
      <figcaption
        style={FONT_STYLES.base}
        className="text-center text-neutral-4"
      >
        {author}
      </figcaption>
    </motion.figure>
  );
};

const SectionSuperQuote = ({
  author,
  className,
  quote,
}: {
  author: string;
  quote: string;
  className?: string;
}) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.figure
      className={classNames(
        'relative flex w-full max-w-120 flex-col items-center justify-center gap-2 justify-self-center p-6 py-24 sm:max-w-160',
        BEFORE_CLASS_GRADIENT,
        AFTER_CLASS_GRADIENT,
        className,
      )}
      initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 24,
      }}
      viewport={{ once: true }}
      whileInView={{
        opacity: 1,
        y: 0,
        transition: {
          type: 'easeOut',
          duration: 0.8,
          delay: 0.2,
        },
      }}
    >
      <blockquote
        className="w-full text-balance text-center"
        style={FONT_STYLES.md}
      >
        {quote}
      </blockquote>
      <figcaption
        className="text-center text-neutral-4"
        style={FONT_STYLES.base}
      >
        {author}
      </figcaption>
    </motion.figure>
  );
};

const CHART_COLORS = ['#FF5207', '#FF7D26'] as const;

const LegendChart = ({ color, legend }: { color: string; legend: string }) => {
  return (
    <span
      role="listitem"
      className="flex items-center gap-2"
      style={{ '--color': color } as CSSProperties}
    >
      <span
        aria-hidden
        className="inline-block h-3 w-3 rounded-full bg-[var(--color)]"
      />
      <span className="text-neutral-4" style={FONT_STYLES.xs}>
        {legend}
      </span>
    </span>
  );
};

const HorizontalBarChart = ({
  data,
}: {
  data: {
    label: string;
    value: number;
  }[];
}) => {
  const shouldReduceMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setIsMobile(entry.contentRect.width <= 448);
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      role="list"
      className="group relative z-20 flex w-full flex-col gap-2"
    >
      {data.map((item, index) => {
        const maxValue = Math.max(...data.map((value) => value.value));
        const width = item.value * (100 / maxValue);

        return (
          <div
            key={`barchart-${item.label}`}
            role="listitem"
            aria-label={`${item.label}: ${item.value}%`}
            className={classNames(
              'flex w-full transition duration-300 ease-out hover:!opacity-100 group-hover:opacity-50',
              isMobile ? 'flex-col gap-1' : 'flex-row items-center gap-3',
            )}
            style={{ '--color': CHART_COLORS[0] } as CSSProperties}
          >
            <span
              aria-hidden
              className={classNames(
                'line-clamp-3 w-full text-balance text-neutral-4',
                isMobile ? 'max-w-full' : 'max-w-[25%] shrink-0',
              )}
              style={FONT_STYLES.xs}
            >
              {item.label}
            </span>
            <span aria-hidden className="w-full">
              <motion.span
                className="flex min-h-12 items-center justify-end rounded-sm bg-[var(--color)]"
                initial={{
                  opacity: shouldReduceMotion ? 1 : 0,
                  width: shouldReduceMotion ? `${width}%` : 0,
                }}
                transition={{
                  ease: 'easeOut',
                  duration: 0.4,
                  delay: index * 0.1,
                }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, width: `${width}%` }}
              >
                <span
                  className={classNames(
                    'flex p-2',
                    width < 10
                      ? 'translate-x-full text-[var(--color)]'
                      : 'text-white',
                  )}
                >
                  {`${item.value}%`}
                </span>
              </motion.span>
            </span>
          </div>
        );
      })}
    </div>
  );
};

const SuperHorizontalBarChart = ({
  data,
  groups,
}: {
  groups: string[];
  data: {
    label: string;
    values: number[];
  }[];
}) => {
  const shouldReduceMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setIsMobile(entry.contentRect.width <= 448);
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const maxValue = Math.max(...data.flatMap((item) => item.values));

  return (
    <div
      ref={containerRef}
      className="relative z-20 flex w-full flex-col gap-4"
    >
      <div role="list" className="group flex w-full flex-col gap-4">
        {data.map((item, rowIndex) => (
          <div
            key={`super-barchart-${item.label}`}
            role="listitem"
            aria-label={`${item.label}: ${groups
              .map((g, gi) => `${g} ${item.values[gi]}%`)
              .join(', ')}`}
            className={classNames(
              'flex w-full transition duration-300 ease-out hover:!opacity-100 group-hover:opacity-50',
              isMobile ? 'flex-col gap-1' : 'flex-row items-center gap-3',
            )}
          >
            <span
              aria-hidden
              className={classNames(
                'line-clamp-3 w-full text-balance text-neutral-4',
                isMobile ? 'max-w-full' : 'max-w-[25%] shrink-0',
              )}
              style={FONT_STYLES.xs}
            >
              {item.label}
            </span>

            <div aria-hidden className="flex w-full flex-col gap-1">
              {item.values.map((value, gi) => {
                const width = value * (100 / maxValue);
                const color = CHART_COLORS[gi % CHART_COLORS.length];
                return (
                  <span
                    key={gi}
                    className="w-full"
                    style={{ '--color': color } as CSSProperties}
                  >
                    <motion.span
                      className="flex min-h-6 items-center justify-end rounded-sm bg-[var(--color)]"
                      initial={{
                        opacity: shouldReduceMotion ? 1 : 0,
                        width: shouldReduceMotion ? `${width}%` : 0,
                      }}
                      transition={{
                        ease: 'easeOut',
                        duration: 0.4,
                        delay: rowIndex * 0.1 + gi * 0.05,
                      }}
                      viewport={{ once: true }}
                      whileInView={{ opacity: 1, width: `${width}%` }}
                    >
                      <span
                        className={classNames(
                          'flex p-2',
                          width < 10
                            ? 'translate-x-full text-[var(--color)]'
                            : 'text-white',
                        )}
                        style={FONT_STYLES.xs}
                      >
                        {`${value}%`}
                      </span>
                    </motion.span>
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div role="list" aria-label="Legend" className="flex flex-col gap-2">
        {groups.map((group, gi) => {
          const color = CHART_COLORS[gi % CHART_COLORS.length];
          return <LegendChart key={group} legend={group} color={color} />;
        })}
      </div>
    </div>
  );
};

const VerticalBarChart = ({
  data,
}: {
  data: {
    label: string;
    value: number;
  }[];
}) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <div role="list" className="group relative z-20 flex w-full gap-2">
      {data.map((item, index) => {
        const maxValue = Math.max(...data.map((value) => value.value));
        const height = item.value * (100 / maxValue);

        return (
          <div
            key={`barchart-${item.label}`}
            role="listitem"
            aria-label={`${item.label}: ${item.value}%`}
            className="flex w-full flex-col gap-1 transition duration-300 ease-out hover:!opacity-100 group-hover:opacity-50"
            style={{ '--color': CHART_COLORS[0] } as CSSProperties}
          >
            <span aria-hidden className="flex h-72 w-full flex-col justify-end">
              <motion.span
                className="flex w-full items-start justify-center rounded-sm bg-[var(--color)]"
                initial={{
                  opacity: shouldReduceMotion ? 1 : 0,
                  height: shouldReduceMotion ? `${height}%` : 0,
                }}
                transition={{
                  ease: 'easeOut',
                  duration: 0.4,
                  delay: index * 0.1,
                }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, height: `${height}%` }}
              >
                <span
                  className={classNames(
                    'flex p-2 text-center',
                    height < 10
                      ? '-translate-y-full text-[var(--color)]'
                      : 'text-white',
                  )}
                >
                  {`${item.value}%`}
                </span>
              </motion.span>
            </span>
            <span
              aria-hidden
              className="line-clamp-3 w-full text-balance text-center text-neutral-4"
              style={FONT_STYLES.xs}
            >
              {item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const SuperVerticalBarChart = ({
  data,
  groups,
}: {
  groups: string[];
  data: {
    label: string;
    values: number[];
  }[];
}) => {
  const shouldReduceMotion = useReducedMotion();
  const maxValue = Math.max(...data.flatMap((item) => item.values));

  return (
    <div className="relative z-20 flex w-full flex-col gap-4">
      <div role="list" className="group flex w-full gap-4">
        {data.map((item, colIndex) => (
          <div
            key={`super-vchart-${item.label}`}
            role="listitem"
            aria-label={`${item.label}: ${groups
              .map((g, gi) => `${g} ${item.values[gi]}%`)
              .join(', ')}`}
            className="flex w-full flex-col items-center gap-2 transition duration-300 ease-out hover:!opacity-100 group-hover:opacity-50"
          >
            <div aria-hidden className="flex h-72 w-full gap-1">
              {item.values.map((value, gi) => {
                const height = value * (100 / maxValue);
                const color = CHART_COLORS[gi % CHART_COLORS.length];
                return (
                  <span
                    key={gi}
                    className="flex h-full w-full flex-col justify-end"
                    style={{ '--color': color } as CSSProperties}
                  >
                    <motion.span
                      className="flex w-full items-start justify-center rounded-sm bg-[var(--color)]"
                      initial={{
                        opacity: shouldReduceMotion ? 1 : 0,
                        height: shouldReduceMotion ? `${height}%` : 0,
                      }}
                      transition={{
                        ease: 'easeOut',
                        duration: 0.4,
                        delay: colIndex * 0.1 + gi * 0.05,
                      }}
                      viewport={{ once: true }}
                      whileInView={{ opacity: 1, height: `${height}%` }}
                    >
                      <span
                        className={classNames(
                          'flex p-2 text-center',
                          height < 15
                            ? '-translate-y-full text-[var(--color)]'
                            : 'text-white',
                        )}
                        style={FONT_STYLES.base}
                      >
                        {`${value}%`}
                      </span>
                    </motion.span>
                  </span>
                );
              })}
            </div>

            <span
              aria-hidden
              className="line-clamp-3 w-full text-balance text-center text-neutral-4"
              style={FONT_STYLES.xs}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>

      <div role="list" aria-label="Legend" className="flex flex-col gap-2">
        {groups.map((group, gi) => {
          const color = CHART_COLORS[gi % CHART_COLORS.length];
          return <LegendChart key={group} legend={group} color={color} />;
        })}
      </div>
    </div>
  );
};

const Page = ({
  backgroundImage,
  locale,
  locales,
  ogImageUrl,
  preview,
  story,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation(['reports']);
  const isMobile = useMediaQuery('sm');

  // Content
  const page = useStoryblok(story, preview, locale);
  const reportDate = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
  }).format(new Date(page.content.date));

  // Images
  const images = story.content.article
    .filter((blocks) => {
      return blocks.component === 'multiImage';
    })
    .map((blocks) => {
      return blocks.images
        .filter((block) => {
          return block.component === 'image';
        })
        .map((block) => {
          return block.image;
        })[0];
    });

  // Download reort
  const ctaRef = useRef<HTMLAnchorElement>(null);
  const sectionSubscriptionRef = useRef<HTMLAnchorElement>(null);
  const subscribeFormRef = useRef<{ focusEmailInput: () => void } | null>(null);

  const handleScrollTo = useCallback(() => {
    if (sectionSubscriptionRef.current) {
      scrollToShortcut(sectionSubscriptionRef);
      setTimeout(() => {
        subscribeFormRef.current?.focusEmailInput();
      }, 600);
    }
  }, []);

  const [toastIsOpened, setToastIsOpened] = useState(false);
  useEffect(() => {
    if (toastIsOpened) {
      setTimeout(() => {
        setToastIsOpened(false);
      }, 5000);
    }
  }, [toastIsOpened]);

  return (
    <Layout
      description={page.content.seo.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={page.content.seo.title}
    >
      <StickyHeader ctaRef={ctaRef}>
        <div className="flex w-full items-center space-x-3">
          <div className="flex w-full flex-col overflow-hidden">
            <p className="line-clamp-2 text-balance text-neutral-4">
              {page.content.title}
            </p>
          </div>
          <div className="shrink-0">
            <Button
              label={t('reports:cta.sticky')}
              onClick={handleScrollTo}
              size={isMobile ? 'sm' : 'md'}
              variant="primary"
            />
          </div>
        </div>
      </StickyHeader>

      <div className="relative mx-auto grid w-full items-center overflow-x-hidden pb-12 md:pb-24">
        <Section hideLine>
          <SectionTitle
            hideLine
            overline={reportDate}
            className="col-span-full max-w-full sm:max-w-full"
          >
            66% of new-gen collectors want to{' '}
            <span className="text-active-4">increase their support</span> for
            museums, but only 1 in 7 hold a membership
          </SectionTitle>
        </Section>

        <Section>
          <div className="theme-dark relative col-span-full flex aspect-[2/3] w-full flex-col items-center justify-end overflow-hidden rounded-lg bg-neutral-6 px-9 py-9 text-white md:aspect-[4/3] md:items-start lg:aspect-[2/1]">
            <div className="absolute inset-0">
              <Image
                alt="TODO"
                className="h-full w-full object-cover"
                size={{
                  sm: { ratio: '2/3', span: 12 },
                  md: { ratio: '4/3', span: 12 },
                  lg: { ratio: '16/9', span: 12 },
                }}
                src={backgroundImage.filename}
                focus={backgroundImage.focus}
              />
            </div>
            <span
              aria-hidden
              className="absolute inset-0 bg-neutral-6/40 md:bg-neutral-6/20"
            />
            <span
              aria-hidden
              className="absolute inset-0 bg-neutral-6/40 backdrop-blur-3xl [maskImage:linear-gradient(0deg,rgba(0,0,0,1)_25%,rgba(0,0,0,0)_50%)] md:[maskImage:linear-gradient(90deg,rgba(0,0,0,1)_10%,rgba(0,0,0,0)_50%)]"
            />

            <div className="relative flex w-full max-w-xs flex-col items-center gap-6 md:items-start">
              <div className="flex w-full flex-col gap-3">
                <p className="text-pretty text-center drop-shadow-lg md:text-left">
                  Through a combination of interviews and in-depth surveys, we
                  engaged over two thousand art collectors, enthusiasts and
                  professionals from around the world to track the emergence of
                  a new generation of art lovers and collectors.
                </p>
              </div>
              <div className="shrink-0">
                <Button
                  label={t('reports:cta.default')}
                  onClick={handleScrollTo}
                  variant="primary"
                />

                <ScrollAnchor ref={ctaRef} id="heroRef" />
              </div>
            </div>
          </div>
        </Section>

        <Section hideLine>
          <div
            className={classNames(
              'relative col-span-full flex flex-col items-center gap-6 py-24 md:gap-12',
              'after:absolute after:bottom-0 after:z-20 after:block after:h-3 after:w-3 after:translate-y-1/2 after:rotate-45 after:bg-active-4 after:content-[""]',
            )}
          >
            <div
              className={classNames(
                'flex w-full max-w-120 flex-col items-center justify-center gap-2 justify-self-center sm:max-w-160',
              )}
            >
              <h2
                className="w-full text-balance text-center font-bold"
                style={FONT_STYLES.md}
              >
                Introduction
              </h2>
            </div>
            <div
              className={classNames(
                'flex w-full max-w-120 flex-col justify-center gap-6 justify-self-center',
              )}
            >
              <p
                className="text-pretty max-md:text-center"
                style={FONT_STYLES.base}
              >
                For this year's report, we spoke with over two thousand art
                collectors and enthusiasts alongside leaders at key institutions
                and museums to understand a generational shift now reshaping the
                art world. The picture that emerges challenges many familiar
                assumptions. This is a new generation of art lovers that, far
                from disengaging from art or institutions, is confidently
                redefining how they discover, collect, visit, support and
                participate.
              </p>
              <p
                className="text-pretty max-md:text-center"
                style={FONT_STYLES.base}
              >
                This year, we've focused specifically on the gap opening up
                between younger collectors (those under 45) and their older
                counterparts, as this new generation takes on a leading role
                across both the art market and institutional philanthropy.
              </p>
              <p
                className="text-pretty max-md:text-center"
                style={FONT_STYLES.base}
              >
                The scale of that shift may surprise. This is a generation
                investing as much in art as in a car or a watch, with many
                spending over a month's salary on a single artwork. Their taste
                is wide, omnivorous, and far less driven by hype than many
                assume. They are building the foundations of their collections
                through editions and prints, finding an accessible entry point
                in an increasingly polarised market. They want to support
                museums financially, but not as passive patrons. Rather, they
                want giving to be tied to engagement, connection, social impact
                and community, not acquisitions and prestige.
              </p>
              <p
                className="text-pretty max-md:text-center"
                style={FONT_STYLES.base}
              >
                Our findings show that this new generation are more serious,
                more knowledgeable, and more willing to spend than many in the
                sector give them credit for. But because art plays a
                fundamentally different role in their lives, they need
                fundamentally different things from the institutions that serve
                them. How they discover, what brings them through the door, what
                they want from memberships, and how they choose to give: in each
                case, the old models are struggling to keep up with a generation
                that is willing to engage, but on its own terms.
              </p>
            </div>
          </div>
        </Section>

        <Section>
          <SectionTitle overline="The role of art" className="col-span-full">
            An underestimated generation
          </SectionTitle>
          <SectionKeyPoint
            index={1}
            text="They are a third more likely to be interested in Old Masters, and 23% more likely to be interested in both Old Masters and contemporary simultaneously"
            title="New-gen are art omnivores, not trend-chasers. "
            className="col-span-full md:col-span-2"
          />
          <SectionQuote
            className="col-span-full md:col-span-2"
            author="Female collector, 25-34, United Kingdom"
            quote="“I buy art that symbolises or brings to life thoughts I've held in my mind. It serves as a reminder.”"
          />
          <SectionChart
            title="How strongly are you interested in the following types of artists?"
            className="col-span-full md:col-span-2"
          >
            <SuperVerticalBarChart
              groups={['18–44 years old', 'Over 45 years old']}
              data={[
                { label: 'Estates', values: [55, 35] },
                { label: 'Old Masters', values: [48, 35] },
              ]}
            />
          </SectionChart>
          <SectionKeyPoint
            index={2}
            text="Art spending is protected even among those citing cost-of-living pressures: only 8% plan to decrease "
            title="40% would spend more than a month's salary on a single work"
            className="col-span-full md:col-span-2"
          />
          <SectionKeyPoint
            index={3}
            text="In spite of cost-of-living pressures, younger collectors are still prioritising art, with 18-24s planning to spend more at three times the rate of over-65s"
            title="Next-gen are 65% more likely to plan an increase in their art budget next year"
            className="col-span-full md:col-span-2"
          />
          <SectionImage
            caption={images[0].alt || ''}
            className="col-span-full md:col-span-2"
          >
            <div className="relative z-20 aspect-[4/3] w-full overflow-hidden rounded-lg">
              <Image
                alt={images[0].alt || ''}
                focus={images[0].focus}
                className="h-full w-full object-cover"
                src={images[0].filename}
              />
            </div>
          </SectionImage>
        </Section>

        <Section>
          <SectionTitle
            overline="Digital and Social Media"
            className="col-span-full"
          >
            The new front door
          </SectionTitle>
          <SectionKeyPoint
            index={4}
            text="Nearly two-thirds use Instagram as their primary discovery channel, whilst they are seven times more likely to use TikTok"
            title="Discovery for this generation is overwhelmingly digital-first"
            className="col-span-full md:col-span-2"
          />
          <SectionChart
            title="How do you typically discover new art and artists?"
            className="col-span-full md:col-span-2"
          >
            <VerticalBarChart
              data={[
                {
                  label: 'Instagram',
                  value: 84,
                },
                {
                  label: 'Visiting museums and galleries',
                  value: 47,
                },
                {
                  label: 'Word of mouth',
                  value: 20,
                },
                {
                  label: 'Press media',
                  value: 17,
                },
                {
                  label: 'TikTok',
                  value: 14,
                },
              ]}
            />
          </SectionChart>
          <SectionImage
            caption={images[1].alt || ''}
            className="col-span-full md:col-span-2"
          >
            <div className="relative z-20 aspect-[4/3] w-full overflow-hidden rounded-lg">
              <Image
                alt={images[1].alt || ''}
                focus={images[1].focus}
                className="h-full w-full object-cover"
                src={images[1].filename}
              />
            </div>
          </SectionImage>
          <SectionKeyPoint
            index={5}
            text="Only 3 in 10 feel museums understand how to engage them on social media, yet 68% have been directly motivated to visit after seeing a post"
            title="Digital is a powerful driver for visits, but it’s still underutilised"
            className="col-span-full md:col-span-2"
          />
          <SectionKeyPoint
            index={6}
            text="71% would visit more often if museums were better at notifying them about relevant exhibitions"
            title="Improve how you reach this generation, and they will show up"
            className="col-span-full md:col-span-2"
          />
          <SectionSuperQuote
            className="col-span-full md:col-span-2"
            author="Design Museum"
            quote="“Digital really is the key way. We launched TikTok a couple of years ago, and obviously that has a younger target audience. We also did a partnership with Snapchat. Our research has shown that you have to communicate with young people through digital channels because that is how they find out about things.”"
          />
        </Section>

        <Section>
          <SectionTitle
            overline="The New Generation and Museums"
            className="col-span-full"
          >
            What brings them in (and back)
          </SectionTitle>
          <SectionKeyPoint
            index={7}
            text="They show up as often as older collectors (44% visit monthly) but for different reasons: 70% more likely to meet friends there, 65% more likely to want parties and galas"
            title="The role of the museum for this generation is shifting"
            className="col-span-full md:col-span-2"
          />
          <SectionChart
            title="Why do you visit museums?"
            className="col-span-full md:col-span-2"
          >
            <SuperHorizontalBarChart
              groups={['18–44 years old', 'Over 45 years old']}
              data={[
                { label: 'See a specific exhibition', values: [46, 55] },
                { label: 'Learn new things', values: [33, 29] },
                { label: 'See favourite artists', values: [29, 35] },
                { label: 'Relax', values: [25, 19] },
                { label: 'Socialise', values: [15, 9] },
              ]}
            />
          </SectionChart>
          <SectionImage
            caption={images[2].alt || ''}
            className="col-span-full md:col-span-2"
          >
            <div className="relative z-20 aspect-[1/1] w-full overflow-hidden rounded-lg">
              <Image
                alt={images[2].alt || ''}
                focus={images[2].focus}
                className="h-full w-full object-cover"
                src={images[2].filename}
              />
            </div>
          </SectionImage>
          <SectionQuote
            className="col-span-full md:col-span-2"
            author="Female collector, 25-34, Belgium"
            quote="“I'd like to see more marginalised people be given a seat at the table: women from migrant backgrounds, queer people, refugees, and those from lower socioeconomic backgrounds.”"
          />
          <SectionSuperQuote
            className="col-span-full md:col-span-2"
            author="LACMA"
            quote="“Older audiences are truly driven just by the art. They want engagement with the art, whereas we see that younger audiences are motivated by opportunities to be with friends and family, mostly friends. They are looking for more social opportunities.”"
          />
          <SectionImage
            caption={images[3].alt || ''}
            className="col-span-full md:col-span-2"
          >
            <div className="relative z-20 aspect-[1/1] w-full overflow-hidden rounded-lg">
              <Image
                alt={images[3].alt || ''}
                focus={images[3].focus}
                className="h-full w-full object-cover"
                src={images[3].filename}
              />
            </div>
          </SectionImage>
          <SectionImage caption={images[4].alt || ''} className="col-span-full">
            <div className="relative z-20 aspect-[2/1] w-full overflow-hidden rounded-lg">
              <Image
                alt={images[4].alt || ''}
                focus={images[4].focus}
                className="h-full w-full object-cover"
                src={images[4].filename}
              />
            </div>
          </SectionImage>
        </Section>

        <Section>
          <SectionTitle overline="Membership" className="col-span-full">
            The membership gap
          </SectionTitle>
          <SectionKeyPoint
            index={8}
            text="Two-thirds want to increase their financial support of museums, even while cancelling memberships that don't fit"
            title="Existing membership models don’t match how this generation wants to give support"
            className="col-span-full md:col-span-2"
          />
          <SectionChart
            title="Why have you cancelled?"
            className="col-span-full md:col-span-2"
          >
            <HorizontalBarChart
              data={[
                {
                  label: 'Not visiting enough',
                  value: 42,
                },
                {
                  label: 'Rising cost of living',
                  value: 37,
                },
                {
                  label: 'Personal circumstances changed',
                  value: 33,
                },
                {
                  label: 'Benefits not relevant',
                  value: 21,
                },
                {
                  label: 'Supported different institution',
                  value: 13,
                },
                {
                  label: 'Programme less compelling',
                  value: 12,
                },
                {
                  label: 'Limited access',
                  value: 9,
                },
              ]}
            />
          </SectionChart>
          <SectionQuote
            className="col-span-full md:col-span-2"
            author="Male collector, 35-44, France"
            quote="“I’d be more likely to join if they made activities that built a community of art enthusiasts and made us feel part of something, not just elevated visitors.”"
          />
          <SectionQuote
            className="col-span-full md:col-span-2"
            author="Female collector, 25-34, United Kingdom"
            quote="“I think they should have events that are not so formal like drinks receptions. These feel very old school and attract a certain crowd that I don’t connect with.”"
          />
        </Section>

        <Section>
          <SectionTitle
            overline="Supporting Institutions"
            className="col-span-full"
          >
            A new model for patronage
          </SectionTitle>
          <SectionChart
            title="How much have you donated to museums or galleries in the last 12 months?"
            className="col-span-full md:col-span-2"
          >
            <VerticalBarChart
              data={[
                {
                  label: 'Less than $50',
                  value: 51,
                },
                {
                  label: '$50-99',
                  value: 21,
                },
                {
                  label: '$100-449',
                  value: 14,
                },
                {
                  label: '$500-999',
                  value: 7,
                },
                {
                  label: '$1,000-5,000',
                  value: 4,
                },
                {
                  label: 'Over $5,000',
                  value: 3,
                },
              ]}
            />{' '}
          </SectionChart>
          <SectionKeyPoint
            index={9}
            text="Among those spending $1,000+ on art, they are 40% more likely to have given $500+ than over-45s"
            title="Nearly two-thirds of the new-generation donated to a museum in the past year"
            className="col-span-full md:col-span-2"
          />
          <SectionKeyPoint
            index={10}
            text="More than half of the new-generation say they have never been asked to donate, or can't remember the last time they were"
            title="The desire to donate is there, but they aren’t being asked"
            className="col-span-full md:col-span-2"
          />
          <SectionChart
            title="What would encourage you to make a donation, or donate again?"
            className="col-span-full md:col-span-2"
          >
            <HorizontalBarChart
              data={[
                {
                  label: 'Exclusive events or out-of-hours access',
                  value: 29,
                },
                {
                  label: 'Early access to buy editions or artwork',
                  value: 22,
                },
                {
                  label:
                    'More regular communication about programmes and impact',
                  value: 21,
                },
                {
                  label:
                    'Opportunities to connect with other supporters and art lovers',
                  value: 21,
                },
                {
                  label: 'Greater recognition of my generosity',
                  value: 7,
                },
              ]}
            />
          </SectionChart>
          <SectionQuote
            className="col-span-full md:col-span-2"
            author="Female collector, 25-34, United Kingdom"
            quote="“I would love to see what the money is used for directly, get an insight into the projects and use of the donations.”"
          />
          <SectionKeyPoint
            index={11}
            text="Edition buyers are twice as likely to subsequently donate and three times more likely to become patrons"
            title="Editions are a proven bridge to deeper institutional support"
            className="col-span-full md:col-span-2"
          />
        </Section>

        <Section>
          <div className="theme-dark relative col-span-full flex w-full flex-col items-center justify-center overflow-hidden rounded-lg bg-neutral-6 px-6 py-36 text-white">
            <ScrollAnchor
              ref={sectionSubscriptionRef}
              id="SectionSubscription"
              offset={-72}
            />
            <div className="absolute inset-0">
              <Image
                alt={images[5].alt || ''}
                focus={images[5].focus}
                src={images[5].filename}
                className="h-full w-full object-cover"
                size={{
                  sm: { ratio: '2/3', span: 12 },
                  md: { ratio: '4/3', span: 12 },
                  lg: { ratio: '16/9', span: 12 },
                }}
              />
            </div>
            <span aria-hidden className="absolute inset-0 bg-neutral-6/80" />
            <div className="relative flex w-full flex-col gap-6 md:gap-9">
              <h4 className="mx-auto w-full max-w-xl text-balance text-center text-[2.5rem] font-bold leading-none tracking-tight md:text-[3.25rem] lg:text-[4.25rem]">
                Download the full survey
              </h4>
              <SubscribeForm ref={subscribeFormRef} />
              <p className="mx-auto w-full max-w-xl text-balance text-center text-neutral-3">
                New Generation Survey · {reportDate}
              </p>
            </div>
          </div>
        </Section>
      </div>
    </Layout>
  );
};

export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<PageProps>> {
  const translations = await pageTranslations(context.locale || 'en', [
    'reports',
  ]);

  const page: StoryResponse<Franchises> = await getStoryblokPage(
    'cdn/stories/insights/articles/new-generation-survey-2026',
    {
      locale: context.locale,
      preview: context.preview,
    },
  );

  if (!page) {
    return {
      notFound: true,
    };
  }

  const story = page.data?.story;
  const heroPicker = story.content.heroPicker[0] as HeroImage;
  const backgroundImage = heroPicker.image;

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: story.content.title,
    imageSrc: backgroundImage.filename,
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
  });

  return {
    props: {
      locale: context.locale || 'en',
      locales: context.locales || [],
      preview: Boolean(context.preview),
      ogImageUrl,
      story,
      backgroundImage,
      ...translations,
    },
  };
}

export default Page;
